import { Component, Input, OnChanges, SimpleChanges, ViewChild, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CodeManagementApiService } from '@core/services/code-management-api.service';
import { IFilterDropdownsForm } from '@core/models/dropdowns.model';
import { HcpcsCodeItemDTO, HcpcsGetResponseDTO, FormDetailDTO, DeactivateCodeResponse } from '@core/models';
import { Table, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { ConfirmationService, MessageService } from 'primeng/api';
import { CodeExpandedViewComponent } from '../code-expanded-view/code-expanded-view.component';
import { CodeFilters } from '@shared/components/models/code-filters.model';

@Component({
  selector: 'app-code-list',
  templateUrl: './code-list.component.html',
  styleUrls: ['./code-list.component.scss'],
  imports: [ 
    CommonModule,
    TableModule,
    ButtonModule,
    TooltipModule,
    ConfirmDialogModule,
    ToastModule,
    CodeExpandedViewComponent
  ],
  providers: [ConfirmationService, MessageService],
  standalone: true
})
export class CodeListComponent implements OnChanges, AfterViewInit {
  @Input() filters: IFilterDropdownsForm | null = null;
  @Input() codeFilters: CodeFilters = {};
  @ViewChild('dt') dataTable: Table | undefined;
  
  readonly amountOfRows = 10;
  readonly defaultSortField = 'code';
  readonly defaultSortOrder = 1;
  readonly defaultSortOrderOnLoad = 1;
  
  // Table data
  codes: HcpcsCodeItemDTO[] = [];
  isLoading = false;
  totalRecords = 0;
  expandedRowKeys: { [key: string]: boolean } = {};

  constructor(
    private codeManagementService: CodeManagementApiService,
    private confirmationService: ConfirmationService,
    private messageService: MessageService
  ) {}

  getRowClass(code: HcpcsCodeItemDTO): string {
    return this.expandedRowKeys[(code as any).code] ? 'expanded-row' : '';
  }

  ngAfterViewInit(): void {
    if (this.dataTable) {
      this.dataTable.sortField = this.defaultSortField;
      this.dataTable.sortOrder = this.defaultSortOrder;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filters']) {
      const curr = changes['filters'].currentValue;
      
      if (!curr) {
        this.codes = [];
        this.totalRecords = 0;
        return;
      }
      
      if (this.dataTable) {
        this.dataTable.first = 0;
        this.dataTable.sortField = this.defaultSortField;
        this.dataTable.sortOrder = this.defaultSortOrder;
      }
      
      if (!changes['filters'].firstChange) {
        const prev = changes['filters'].previousValue;
        
        if (prev?.tenantId !== curr?.tenantId || 
            prev?.productId !== curr?.productId || 
            prev?.overrideTypeId !== curr?.overrideTypeId) {
          this.expandedRowKeys = {};
          if (this.dataTable) {
            this.dataTable.sortSingle();
          }
        }
      }
    }
    
    if (changes['codeFilters'] && !changes['codeFilters'].firstChange) {
      if (this.dataTable) {
        this.dataTable.first = 0;
        this.dataTable.reset();
      }
    }
  }

  onLazyLoad(event: any): void {
    if (!this.filters) {
      this.codes = [];
      this.totalRecords = 0;
      this.isLoading = false;
      return;
    }
    
    this.isLoading = true;
    
    const first = event.first || 0;
    const rows = event.rows || this.amountOfRows;
    const currentPage = Math.floor(first / rows) + 1;
    const sortBy = event.sortField || this.defaultSortField;
    const sortOrder = event.sortOrder === 1 ? 'asc' : event.sortOrder === -1 ? 'desc' : 'asc';
    
    const request: any = {
      tenantId: this.filters.tenantId,
      productId: this.filters.productId,
      overrideTypeId: this.filters.overrideTypeId,
      pageNumber: currentPage,
      pageSize: rows,
      sortBy: sortBy,
      sortOrder: sortOrder
    };
    
    if (this.codeFilters && Object.keys(this.codeFilters).length > 0) {
      this.applyCodeFiltersToRequest(request);
    }
    
    this.codeManagementService.getCodes(request).subscribe({
      next: (response: HcpcsGetResponseDTO) => {
        this.codes = response.items || [];
        this.totalRecords = response.totalRecords || 0;
        this.isLoading = false;
      },
      error: (error: any) => {
        this.isLoading = false;
      }
    });
  }

  toggleRow(codeKey: string): void {
    if (this.expandedRowKeys[codeKey]) {
      this.expandedRowKeys = {};
    } else {
      this.expandedRowKeys = { [codeKey]: true };
    }
  }
  
  getFormsTooltip(code: HcpcsCodeItemDTO): string {
    if (!code.formDetails || code.formDetails.length === 0) {
      return '';
    }
    return code.formDetails.map((form: FormDetailDTO) => form.formTitle).join('\n');
  }

  private applyCodeFiltersToRequest(request: any): void {
    const filters = this.codeFilters as any;
    
    const stringFields = ['code', 'shortDescription', 'formTitle'];
    stringFields.forEach(field => {
      if (filters[field]) {
        request[field] = filters[field];
      }
    });
    
    if (filters.formId) {
      request.FormIoId = filters.formId;
    }
    
    if (filters.status) {
      request.modifierStatus = filters.status;
    }

    if (filters.cmsEffectiveStartDateFrom) {
      request.cmsEffectiveStartDate = this.formatDateForApi(filters.cmsEffectiveStartDateFrom);
    }
    if (filters.cmsEffectiveStartDateTo) {
      request.cmsEffectiveEndDate = this.formatDateForApi(filters.cmsEffectiveStartDateTo);
    }
    
    if (filters.planEffectiveStartDateFrom) {
      request.planEffectiveStartDate = this.formatDateForApi(filters.planEffectiveStartDateFrom);
    }
    if (filters.planEffectiveStartDateTo) {
      request.planEffectiveEndDate = this.formatDateForApi(filters.planEffectiveStartDateTo);
    }
    
    if (filters.modifierEffectiveStartDateFrom) {
      request.modifierEffectiveStartDate = this.formatDateForApi(filters.modifierEffectiveStartDateFrom);
    }
    if (filters.modifierEffectiveStartDateTo) {
      request.modifierEffectiveEndDate = this.formatDateForApi(filters.modifierEffectiveStartDateTo);
    }
    
    if (filters.formEffectiveStartDateFrom) {
      request.formEffectiveStartDate = this.formatDateForApi(filters.formEffectiveStartDateFrom);
    }

    const booleanFields = ['delegated', 'paRequired', 'paAllowed', 'sharedForm'];
    booleanFields.forEach(field => {
      if (filters[field] !== null && filters[field] !== undefined) {
        request[field] = filters[field] === true ? 'Yes' : 'No';
      }
    });

    if (filters.modifiers && filters.modifiers.length > 0) {
      request.modifiers = filters.modifiers.join(',');
    }
  }

  private formatDateForApi(date: Date | null | undefined): string | undefined {
    if (!date) return undefined;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  onDeleteCode(code: HcpcsCodeItemDTO): void {
    this.confirmationService.confirm({
      header: 'Delete Code',
      message: `<strong>Are you sure you want to delete code ${code.code}?</strong><br/><br/>This removes the code from all programs and can't be undone.`,
      icon: '',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => {
        this.deleteCode(code);
      }
    });
  }

  private deleteCode(code: HcpcsCodeItemDTO): void {
    if (!this.filters?.tenantId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Tenant ID is required'
      });
      return;
    }

    this.isLoading = true;
    this.codeManagementService.deleteCode(code.hcpcsId, this.filters.tenantId).subscribe({
      next: (response: DeactivateCodeResponse) => {
        this.isLoading = false;
        
        if (response.result === 'success') {
          this.messageService.add({
            severity: 'success',
            summary: 'Code Deleted',
            detail: 'HCPCS code was successfully deleted from all programs.',
            life: 3000
          });
          if (this.dataTable) {
            this.dataTable.reset();
          }
        } else if (response.result === 'InUseByAuth') {
          this.messageService.add({
            severity: 'warn',
            summary: 'Unable to Delete',
            detail: `This code is used by an authorization(s) and can\'t be deleted.`,
            life: 5000
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Delete Failed',
            detail: response.data?.[0] || 'Failed to delete code',
            life: 5000
          });
        }
      },
      error: (error) => {
        this.isLoading = false;
        console.error('Error deleting code:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'An error occurred while deleting the code',
          life: 5000
        });
      }
    });
  }
}
